import { Outlet, useMatch } from 'react-router-dom';
import SubMenu, { SubMenuTitle } from 'components/SubMenu';
import { BulkSmsAccessPolicy, isAuthorized } from 'constants/Roles';
import Routes from 'constants/Routes';
import useMe from 'contexts/authentication/useMe';
import useHasAccessToHRSpeedingReport from 'hooks/useHasAccessToHRSpeedingReport';
import QualityFlagsPage from 'pages/AfterControl/QualityFlags';

/**
 * Layout för menyfliken "Kvalitet (BETA)". Undersidorna renderas via Outlet:
 *  - index: Godkänt med anmärkning (QualityFlagsPage)
 *  - /kvalitet/nekade-uppdrag: Nekade uppdrag — avböjningar gjorda i appen,
 *    med uppföljningsunderlag per fälttestare (DeclinedAssignmentsPage)
 *  - /kvalitet/sms-utskick: mass-SMS till fälttestare, valfritt begränsat till en
 *    eller flera grupper (BulkSmsPage). Snävare rollkrav än övriga flikar, se
 *    BulkSmsAccessPolicy.
 *  - /kvalitet/hastighet-gps: rapporten Hastighet & GPS (flyttad hit från HR
 *    2026-09, finns även under Efterkontroll), fortsatt bakom feature-flaggan
 *    HRSpeedingReportEnabled.
 *  - /kvalitet/uppdrag/:id: ärendemodalen för ett flaggat uppdrag. Modalen
 *    ligger under Kvalitet (inte under Efterkontroll) så att den som bara har
 *    kvalitetsrollen kan öppna uppdraget utan att slås ut av Efterkontrolls
 *    rollspärr.
 *
 * QualityFlagsPage renderas av layouten i stället för via en index-route, så
 * att listan (med sina filter) ligger kvar bakom ärendemodalen och är oförändrad
 * när modalen stängs.
 */
const QualityPage = () => {
  const me = useMe();
  const canSeeSpeeding = useHasAccessToHRSpeedingReport();
  const canSendBulkSms = isAuthorized(
    BulkSmsAccessPolicy.allow,
    BulkSmsAccessPolicy.deny,
    me?.roles
  );

  const isDeclinedRoute = useMatch(Routes.quality.declined);
  const isSpeedingRoute = useMatch(Routes.quality.speeding);
  const isBulkSmsRoute = useMatch(Routes.quality.bulkSms);
  const showQualityFlags =
    !isDeclinedRoute && !isSpeedingRoute && !isBulkSmsRoute;

  const items = [
    {
      label: <SubMenuTitle>Godkänt med anmärkning</SubMenuTitle>,
      path: Routes.quality.index,
    },
    {
      label: <SubMenuTitle>Nekade uppdrag</SubMenuTitle>,
      path: Routes.quality.declined,
    },
    ...(canSeeSpeeding
      ? [
          {
            label: <SubMenuTitle>Hastighet & GPS</SubMenuTitle>,
            path: Routes.quality.speeding,
          },
        ]
      : []),
    ...(canSendBulkSms
      ? [
          {
            label: <SubMenuTitle>SMS-utskick</SubMenuTitle>,
            path: Routes.quality.bulkSms,
          },
        ]
      : []),
  ];

  return (
    <>
      <SubMenu items={items} />
      {showQualityFlags && <QualityFlagsPage />}
      <Outlet />
    </>
  );
};

export default QualityPage;
